import FontBody1 from "../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR } from "../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../components/form-field-line/form-field-line";
import { IOption, SelectDropdown } from "../../../../components/inputs/select-dropdown/select-dropdown";
import { DefaultModalLayout } from "../../../../components/modal/layouts/default-modal-layout";
import { Input } from "../../../../components/inputs/input/input";
import { BUTTON_VARIANTS } from "../../../../components/inputs/button-variants";
import { Button } from "../../../../components/inputs/button/button";
import { observer } from "mobx-react";
import './configuration-modal.scss';
import Icon from "../../../../components/icons/icon/icon";
import IconCheck from "../../../../components/icons/icon-check/icon-check";
import FontBody2 from "../../../../components/fonts/font-body2/font-body2";
import portalStore from "../../../../stores/current-portal-store";

interface IConfigurationModalProps {
    onCloseClicked: () => void,
    onSaveClicked: () => void,
}

export const ConfigurationModal: React.FC<IConfigurationModalProps> = observer(({ onCloseClicked, onSaveClicked }) => {
    const setting = portalStore.setting;
    
    const payedByArray = ['customer', 'store'];
    const productCostMarginArray = ['0-20', '20-40', '40-60', '60-80', '80-100'];
    const returnedItemValueArray = Array(11).fill(0).map((n, i) => n + i * 10);

    const getPayedByTypeList = (): IOption[] => {
        return payedByArray.map(margin => ({
            label: `The ${margin}`,
            value: margin,
        }))
    }
    const getCostMarginList = (): IOption[] => {
        return productCostMarginArray.map(margin => ({
            label: `${margin}%`,
            value: margin,
        }))
    }
    const getReturnedItemValueList = (): IOption[] => {
        return returnedItemValueArray.map(itemValue => ({
            label: `${itemValue}%${itemValue === 80 ? ` (Recommended)` : ``}`,
            value: itemValue,
        }))
    }

    const payedByValue = setting?.returnPaidBy ? {
        value: setting?.returnPaidBy,
        label: getPayedByTypeList().find(type => type.value === setting?.returnPaidBy)?.label ?? '',
    } as IOption : undefined;

    const productCostMarginValue = setting?.storeCostMargin ? {
        value: setting?.storeCostMargin,
        label: getCostMarginList().find(type => type.value === setting?.storeCostMargin)?.label ?? '',
    } as IOption : undefined;

    const returnedItemvalue = setting?.returnedItemValue !== undefined ? {
        value: setting?.returnedItemValue,
        label: getReturnedItemValueList().find(type => type.value === setting?.returnedItemValue)?.label ?? '',
    } as IOption : undefined;

    const payedByValid = !!setting?.returnPaidBy?.match(/^.+$/);
    const costMarginValid = !!setting?.storeCostMargin?.match(/^.+$/);
    const itemValueValid = !!String(setting?.returnedItemValue)?.match(/^\d+$/);
    const costOfHandlingAReturnValid = !!String(setting?.costOfHandlingAReturn)?.match(/^\d+$/);

    return <DefaultModalLayout onCloseClicked={() => onCloseClicked()} isCloseDisabled={false}>
        <div className="configuration-modal">
            <div className="configuration-modal-title">
                <FontBody1 color={FONT_COLOR.NEUTRAL3}>ReturnScore settings</FontBody1>
            </div>
            <div className="configuration-modal-implication-text">
                <FontBody1 color={FONT_COLOR.PRIMARY3_50}>
                    You must complete all the ReturnScore settings to enable instant credit resolutions
                </FontBody1>
            </div>

            <div className="configuration-form">
                <FormFieldLine label="Who pays for return shipping">
                    <div className="setting-validated">
                        <SelectDropdown
                            options={getPayedByTypeList()}
                            value={payedByValue}
                            onChange={(e) => setting?.setReturnPaidBy(e?.value as string)}
                            target={null}
                        />
                        <Icon
                            Component={IconCheck}
                            disabled={!payedByValid}
                        />
                    </div>

                </FormFieldLine>

                <FormFieldLine 
                    label="Average product cost margin"
                    tooltip={costMarginTooltip}>
                    <div className="setting-validated">
                        <SelectDropdown
                            options={getCostMarginList()}
                            value={productCostMarginValue}
                            onChange={(e) => setting?.setStoreCostMargin(e?.value as string)}
                            target={null}
                        />
                        <Icon
                            Component={IconCheck}
                            disabled={!costMarginValid}
                        />
                    </div>
                </FormFieldLine>

                <FormFieldLine 
                    label="Returned items value" 
                    tooltip={returnedItemvalueTooltip}>
                    <div className="setting-validated">
                        <SelectDropdown
                            options={getReturnedItemValueList()}
                            value={returnedItemvalue}
                            onChange={(e) => setting?.setReturnedItemValue(e?.value as number)}
                            target={null}
                        />
                        <Icon
                            Component={IconCheck}
                            disabled={!itemValueValid}
                        />
                    </div>
                </FormFieldLine>

                <FormFieldLine
                    label="Cost of handling a return"
                    tooltip={costOfHandlingAReturnTooltip}>
                    <div className="setting-validated">
                        <Input adornment={setting?.currencyCode}
                            value={setting?.costOfHandlingAReturn}
                            onChange={(e) => setting?.setCostOfHandlingAReturn(e as number)}
                            required />
                        <Icon
                            Component={IconCheck}
                            disabled={!costOfHandlingAReturnValid}
                        />
                    </div>
                </FormFieldLine>
            </div>

            <div className="confirm-modal-buttons">
                <Button 
                    text={`Cancel`} 
                    onClick={onCloseClicked} 
                    disabled={setting?.isReturnScoreSaving()}
                    variant={BUTTON_VARIANTS.Tertiary} />
                <Button 
                    text={'Save'} 
                    onClick={onSaveClicked} 
                    variant={BUTTON_VARIANTS.Primary} 
                    hasLoader={true}
                    isLoading={setting?.isReturnScoreSaving()}
                    disabled={ 
                        !payedByValid
                        || !costMarginValid
                        || !itemValueValid
                        || !costOfHandlingAReturnValid
                    }
                />
            </div>
        </div>
    </DefaultModalLayout>;
})

const costMarginTooltip = (
    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
        Average Product Cost Margin [Mandatory]
        <br />
        <br />
        ReturnGO makes different store-credit offers for each return, considering many factors.
        <br />
        <br />
        The Average Product Cost Margin helps us calculate the real value of a store-credit.
        <br />
        For high margin stores, the store-credits' cost is lower, and merchants can afford to give more of them.
        <br />
        For low margin stores, the store-credits' cost is higher, so giving customers more credits is more expensive for the merchant.
        <br />
        <br />
        The Average Product Cost Margin represents the actual average margin that your store is normally selling at.
        <br />
        The exact calculation is: (average_revenue – average_product_cost) / average_revenue
        <br />
        <br />
        But your rough estimate would be great!
    </FontBody2>
);

const returnedItemvalueTooltip = (
    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
        Returned item value [Mandatory]
        <br />
        ReturnGO makes different store-credit offers for each return, considering many factors.
        <br />
        <br />
        The Returned Item Value is the percent of the price that the item retains after it is restocked.
        <br />
        For example, if an item is normally sold for 100$, but an “opened” item can only be sold for 80$, then the value is 80%.
        <br />
        Knowing the real value of a returned product allows ReturnGO to understand the trade-offs and make better store-credit offers.
        <br />
        A value of 100% indicates that the item doesn’t lose any value after it’s returned.
        <br />
        A value of 0% indicates that a returned item is worthless and cannot be resold.
        <br />
        <br />
        Your rough estimate would be great!
    </FontBody2>
);

const costOfHandlingAReturnTooltip = (
    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
        Cost of handling a return [Mandatory]
        <br />
        ReturnGO makes different store-credit offers for each return, considering many factors.
        <br />
        <br />
        The cost of handling a return tells us how much it costs you to receive
        an item back. This
        <br />
        includes labor to unpack, examine, sort, and restock the item, but does not include
        <br />
        shipment back.
        <br />
        Knowing the real cost of the return allows ReturnGO to understand the trade-offs
        <br />
        and make better store-credit offers.
        <br />
        <br />
        Your rough estimate would be great!
    </FontBody2>
);